import React from 'react'
import { makeStyles, CircularProgress, useMediaQuery } from '@material-ui/core'
import Slider from 'react-slick'
import moment from 'moment'

import AnnouncementsSVG from '../../assets/icons/announcements.svg'
import commentFilledSVG from '../../assets/icons/comment-filled-icon.svg'
import heartFilledSVG from '../../assets/icons/heart-filled-icon.svg'
import calendarSVG from '../../assets/icons/calendar.svg'
import emptyDataPreviewSVG from '../../assets/icons/illustration-megaphone-exclamation.svg'
import PhotoSVG from '../../assets/icons/photo.svg'
import styled from '@emotion/styled'

import UserAvatar from '../shared/UserAvatar'
import StyledSVG from '../shared/StyledSVG'

const useStyles = makeStyles(theme => ({
  root: {
    '& .slick-slide': {
      padding: '0px 10px 0px 10px',
      width: 450
    },
    '& .slick-list': {
      width: '100%',
      margin: '0px 10px 0px 0px'
    },
    '& .slick-dots': {
      bottom: '-30px'
    },
    marginBottom: 20
  },
  title: {
    color: '#386093',
    fontSize: 28,
    fontWeight: 600,
    margin: '0px 0px 10px 9px',
    [theme.breakpoints.down('920')]: {
      margin: '20px 0px'
    },
    [theme.breakpoints.down('600')]: {
      margin: '20px 0px',
      textAlign: 'center'
    }
  },
  event_card: {
    padding: '16px 32px',
    borderRadius: 16,
    minHeight: 350
  },
  label: {
    margin: 0,
    fontWeight: 600,
    fontSize: 20,
    color: '#555555'
  },
  icon_wrapper: {
    width: 40,
    height: 40,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    '& path': {
      stroke: '#386093'
    }
  },
  label_section: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  event_overview: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 24
  },
  event_interaction: {
    display: 'flex',
    alignItems: 'center',
    gap: 16
  },
  event_interaction_item: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  horizontal_bar: {
    height: 1,
    width: '100%',
    borderBottom: '1px solid #868E964D'
  },
  event_content: {
    marginTop: 16,
    overflow: 'auto',
    height: 240,
    '&::-webkit-scrollbar': {
      width: '5px'
    },
    '&::-webkit-scrollbar-track': {
      '-webkit-border-radius': '10px',
      'border-radius': '10px'
    },
    '&::-webkit-scrollbar-thumb': {
      '-webkit-border-radius': '10px',
      'border-radius': '10px',
      background: 'rgba(173, 184, 204, 1)'
    }
  },
  event_content_author: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    '& .author': {
      fontWeight: 500,
      fontSize: 16,
      wordBreak: 'break-word'
    }
  },
  empty_data_wrapper: {
    height: 370,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    rowGap: 16,
    '& .description': {
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      '& h1': {
        margin: 0,
        color: '#386093',
        fontSize: 24
      },
      '& p': {
        margin: 0,
        fontWeight: 400,
        fontSize: 16,
        maxWidth: 460
      }
    }
  },
  event_description: {
    '& video': {
      width: '100%',
      height: 'auto'
    },
    '& img': {
      width: '100%',
      height: 'auto'
    },
    '& iframe': {
      width: '100% !important',
      aspectRatio: '16 / 9',
      height: 'auto !important',
      display: 'block',
      margin: '0 auto'
    }
  }
}))

const EventCard = styled('div')(({ bgColor, maxHeight }) => ({
  padding: '16px 32px',
  borderRadius: 16,
  minHeight: 350,
  maxHeight: maxHeight,
  backgroundColor: bgColor,
  cursor: 'pointer'
}))

const EventsSlider = ({ getEventsRSPA, history, isTeacher }) => {
  const [eventItems, setEventItems] = React.useState({
    isFetching: false,
    items: []
  })
  const [isDragging, setIsDragging] = React.useState(false)
  const isMobile = useMediaQuery('(max-width:880px)')
  const isTablet = useMediaQuery('(max-width:1400px)')
  const slidesToShow = isMobile ? 1 : isTablet ? 2 : 2
  const classes = useStyles()

  React.useEffect(async () => {
    fetchEvent()
  }, [])

  async function fetchEvent () {
    setEventItems(prev => ({ ...prev, isFetching: true }))
    await getEventsRSPA({
      params: {
        'q[event_type_not_eq]': 'events',
        'page[size]': 10,
        'q[s]': 'created_at desc',
        includes: 'event_comments,event_likes,user'
      },
      isTeacher
    })
      .then(res => {
        setEventItems(prev => ({ isFetching: false, items: res.data }))
      })
      .catch(() => {
        setEventItems(prev => ({ isFetching: false, items: [] }))
      })
  }

  const handleClickItem = (e, eventId) => {
    if (isDragging) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    history.push(`/announcements/${eventId}`)
  }

  const getEventIcon = eventType => {
    if (eventType === 'communications') {
      return {
        icon: AnnouncementsSVG,
        label: 'Comunicado',
        bgColor: '#D9E8FA'
      }
    } else if (eventType === 'images') {
      return { icon: PhotoSVG, label: 'Mídia', bgColor: '#E8E8DB' }
    }
  }

  return (
    <div className={classes.root}>
      <h1 className={classes.title}>Mural digital</h1>
      {eventItems.isFetching ? (
        <div className={classes.empty_data_wrapper}>
          <CircularProgress />
        </div>
      ) : eventItems.items.length > 0 ? (
        <Slider
          infinite={false}
          dots
          slidesToShow={slidesToShow}
          slidesToScroll={slidesToShow}
          beforeChange={() => setIsDragging(true)}
          afterChange={() => setIsDragging(false)}
        >
          {eventItems.items.map(event => {
            const iconByEventType = getEventIcon(event.event_type)
            return (
              <EventCard
                onClick={ev => handleClickItem(ev, event.id)}
                bgColor={iconByEventType?.bgColor}
                maxHeight={500}
                key={event.id}
              >
                <div className={classes.label_section}>
                  <div className={classes.icon_wrapper}>
                    <StyledSVG
                      src={iconByEventType?.icon}
                      width={20}
                      height={20}
                    />
                  </div>
                  <p className={classes.label}>{iconByEventType?.label}</p>
                </div>
                <div className={classes.event_overview}>
                  <div className={classes.event_interaction}>
                    <div className={classes.event_interaction_item}>
                      <StyledSVG src={heartFilledSVG} width={20} height={20} />
                      <span>{event?.event_likes?.length}</span>
                    </div>
                    <div className={classes.event_interaction_item}>
                      <StyledSVG
                        src={commentFilledSVG}
                        width={20}
                        height={20}
                      />
                      <span>{event?.event_comments?.length}</span>
                    </div>
                  </div>
                  <div className={classes.event_interaction_item}>
                    <StyledSVG src={calendarSVG} width={20} height={20} />
                    <span>{moment(event.created_at).format('DD/MM/YYYY')}</span>
                  </div>
                </div>
                <div className={classes.horizontal_bar} />
                <div className={classes.event_content}>
                  <div className={classes.event_content_author}>
                    <UserAvatar
                      avatarStyle={{ width: 40, height: 40 }}
                      avatarUrl={event?.user?.avatar_url}
                      userName={event?.user?.name}
                      withoutUserName
                    />
                    <p className='author'>{event?.user?.name}</p>
                  </div>
                  <p
                    dangerouslySetInnerHTML={{ __html: event.description }}
                    className={classes.event_description}
                  />
                </div>
              </EventCard>
            )
          })}
        </Slider>
      ) : (
        <div className={classes.empty_data_wrapper}>
          <StyledSVG src={emptyDataPreviewSVG} width={160} height={160} />
          <div className='description'>
            <h1>Seu mural está vazio</h1>
            <p>
              Este é o seu mural digital. Nele, a escola compartilha avisos,
              mensagens e informações importantes para você.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export default EventsSlider
